import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel } from 'components/containers';
import { OptionallyVisible } from 'components/optionallyVisible';

import { ChargesCryptoExchange, CryptoAsset } from 'types/pwcOrder';

import { formatDateLocal } from 'utils/date';
import { capitalizeFirstLetter } from 'utils/string';

import { KEYS, LABELS } from '../../keys';
import { OrderPwcSectionProps } from '../../types';
import { AmountBlock, AmountNumericValue, AmountValue, Item, ItemName, ItemValue, SectionWrapper } from '../styles';
import { formatCryptoCode, formatNumber } from '../utils';

export const ChargeExchangeDetailsSection = ({ orderData, rowId, isS4cUser }: OrderPwcSectionProps) => {
  const { t } = useTranslation();

  const cryptoAsset = orderData?.charges.find((charge) => charge.uuid === rowId)?.cryptoAsset as CryptoAsset | null;
  const cryptoExchange = orderData?.charges.find((charge) => charge.uuid === rowId)?.cryptoExchange as ChargesCryptoExchange;

  const formatExchangeStatus = (str: string | null | undefined): string => {
    return (str ?? '')
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Panel label={t(LABELS.SECTIONS.EXCHANGE_DETAILS.TITLE)}>
      <SectionWrapper>
        <OptionallyVisible visible={isS4cUser}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.EXCHANGE_DETAILS.LIQUIDITY_PROVIDER)}</ItemName>
            <ItemValue>
              {cryptoExchange?.liquidityProvider ? capitalizeFirstLetter(cryptoExchange?.liquidityProvider) : t(KEYS.DASH)}
            </ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(cryptoAsset)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.ORDER.CRYPTO_PRICE)}</ItemName>
            <ItemValue>
              <AmountBlock>
                <AmountNumericValue>{KEYS.BASE_CURRENCY_UNIT}</AmountNumericValue>
                <AmountValue>{formatCryptoCode(cryptoAsset?.cryptoCurrencyCode)}</AmountValue>
                <AmountValue>{KEYS.APPROX_EQUAL_SYMBOL}</AmountValue>
                <AmountNumericValue>{formatNumber(cryptoExchange?.cryptoPrice)}</AmountNumericValue>
                <AmountValue>{orderData.general.fiatCurrencyCode}</AmountValue>
              </AmountBlock>
            </ItemValue>
          </Item>
        </OptionallyVisible>

        <Item>
          <ItemName>{t(LABELS.SECTIONS.EXCHANGE_DETAILS.DEPOSIT_ADDRESS)}</ItemName>
          <ItemValue>{cryptoExchange?.toWalletAddress}</ItemValue>
        </Item>

        <OptionallyVisible visible={Boolean(cryptoExchange?.toWalletMemo)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.EXCHANGE_DETAILS.DEPOSIT_MEMO)}</ItemName>
            <ItemValue>{cryptoExchange?.toWalletMemo}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(cryptoExchange?.depositReceivedAt)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.EXCHANGE_DETAILS.DEPOSIT_RECEIVED_TIME)}</ItemName>
            <ItemValue>{formatDateLocal(cryptoExchange!.depositReceivedAt!)}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(cryptoExchange?.exchangeStatus)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.EXCHANGE_DETAILS.EXCHANGE_STATUS)}</ItemName>
            <ItemValue>{formatExchangeStatus(cryptoExchange?.exchangeStatus)}</ItemValue>
          </Item>
        </OptionallyVisible>
      </SectionWrapper>
    </Panel>
  );
};
